import { UDDS_FLAT_PACKAGING } from '@/src/constants'
import useProductInventory from '@/src/hooks/useProductInventory'
import { useDispensing, useProducts } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { DateInputField, FormButton, FormInputField, Modal, SelectInputField } from '@ui'
import { capitalizeDrugName, getError, showToast } from '@util'
import { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { InferType, object, string } from 'yup'

type Props = {
  onClose: () => void
  id?: string
  drugName?: string
  frequency?: string
  startDate?: string
  endDate?: string
  quantity?: string
  dispenseId?: string
  drugQuery?: string
  product_id?: string
}

const schema = object({
  endDate: string(),
  expire_date: string(),
  frequency: string(),
  note: string(),
  product: string().required('Medication is required'),
  product_inventory: string().required('batch number is required'),
  qty_dispensed: string().required('Quantity is required'),
  packaging: string(),
  startDate: string(),
  supply_duration: string()
})

export const PrepareUnit = ({
  onClose,
  id,
  dispenseId,
  drugQuery,
  endDate,
  frequency,
  quantity,
  startDate,
  product_id
}: Props) => {
  const { dispense, singleDispense, dispenseIsLoading, updateDispense } = useDispensing({ id: dispenseId })
  const { data } = singleDispense ?? {}
  const { attributes, relationships } = data ?? {}
  const methods = useForm({
    resolver: yupResolver(schema),
    values: {
      endDate,
      frequency:
        frequency ||
        attributes?.medication_details.medication_frequency_code ||
        attributes?.medication_details.custom_frequency.interval,
      note: attributes?.note,
      product: relationships?.product.data?.id || '',
      product_inventory: relationships?.product_inventory.data?.id || '',
      qty_dispensed: quantity || attributes?.qty_dispensed || '',
      startDate,
      supply_duration: attributes?.supply_duration,
      packaging: attributes?.packaging
    }
  })

  const { products, included } = useProducts({ query: drugQuery || attributes?.medication_details.drug_name })
  const { inventories, includedData } = useProductInventory({
    product_id
  })
  const { handleSubmit, watch, setValue, formState } = methods
  const product = watch('product')
  const batchId = watch('product_inventory')

  const batchOptions = useMemo(() => {
    if (product && inventories) {
      return inventories.map(({ attributes: { batch_number }, id }) => ({
        id,
        name: batch_number
      }))
    }
    return []
  }, [product, inventories])

  const productOptions = useMemo(() => {
    const dosage_form = includedData && includedData[0]?.attributes?.dosage_form
    if (inventories) {
      return inventories?.map(({ attributes: { drug_name, strength, api_strengths }, id }) => ({
        id,
        name: `${dosage_form} ${capitalizeDrugName(drug_name)}  ${strength || api_strengths?.map(({ strength }) => strength).join('/')}`
      }))
    }
    return []
  }, [inventories, includedData])

  useEffect(() => {
    if (batchId && included) {
      const expireDate = included.find(({ id }) => id === batchId)?.attributes.expiry_date
      setValue('expire_date', expireDate)
    }
  }, [batchId, included, setValue])

  const onSubmit = async ({ product, product_inventory, qty_dispensed, note }: InferType<typeof schema>) => {
    const payload = {
      data: {
        attributes: {
          dispensary_unit: 'ICU',
          packaging: 'bag',
          qty_dispensed,
          ...(note && { note }),
          dispensary_type: 'udds'
        },
        relationships: {
          ...(id && {
            medication: {
              data: {
                id,
                type: 'medication'
              }
            }
          }),
          product: {
            data: {
              id: product_id,
              type: 'product'
            }
          },
          product_inventory: {
            data: {
              id: product_inventory,
              type: 'product_inventory'
            }
          }
        },
        type: 'dispensing'
      }
    }

    try {
      dispenseId ? await updateDispense({ id: dispenseId, payload }) : await dispense(payload)
      showToast(dispenseId ? 'UoU Dose Successfully Updated' : 'UoU Dose Prepared Successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Prepare Unit Dose">
        <form className="flex flex-col overflow-auto w-full max-w-[600px]" onSubmit={handleSubmit(onSubmit)}>
          <div className="overflow-auto flex flex-col gap-4 p-6">
            <SelectInputField
              className="flex flex-col"
              label="Medication "
              labelKey={'name'}
              name="product"
              options={productOptions}
              valueKey={{ key: 'id', reduce: true }}
            />
            <div className="">
              <SelectInputField
                label="Packaging Type"
                name="packaging"
                options={UDDS_FLAT_PACKAGING}
                placeholder="E.g sachet, Unit dose cup, syringe"
                type="number"
              />
            </div>
            <div className="">
              <FormInputField disabled label="Frequency" name="frequency" />
            </div>
            <div className="">
              <FormInputField label="Quantity" name="qty_dispensed" type="number" />
            </div>
            <SelectInputField
              className="flex flex-col"
              label="Batch Number"
              labelKey={'name'}
              name="product_inventory"
              options={batchOptions}
              valueKey={{ key: 'id', reduce: true }}
            />
            <div className="">
              <DateInputField label="Expire Date" name="expire_date" />
            </div>

            {/* <div className="">
              <DateInputField label="Preparation Date and Time" name="time" showTimeSelect showTimeSelectOnly />
            </div> */}
          </div>
          <FormButton isLoading={formState.isSubmitting} onClose={onClose} submit="Save" />
        </form>
      </Modal>
    </FormProvider>
  )
}
